import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse,
} from "@angular/common/http";
import { ErrorHandler, inject } from "@angular/core";
import { Store } from "@ngrx/store";
import { EMPTY, from, Observable, throwError } from "rxjs";
import { catchError, map, mergeMap, switchMap } from "rxjs/operators";

import { environment } from "../../../environments/environment";
import { AuthModeService } from "../_services/auth-mode.service";
import { AuthenticationService } from "../_services/authentication.service";
import { LoaderActions } from "../store/loader";
import { AppState } from "../store/store";

// URL prefixes the portal bearer token may be attached to. Absolute external
// URLs (e.g. Azure SAS blob endpoints used by the v2 upload flow) match none of
// these and must never receive the token, including on the 401-retry path.
const shouldIncludeToken = [
  environment.developmentUrl,
  environment.identityDevelopmentUrl,
  "api",
];

const buildUrl = (url: string): string => {
  if (url.startsWith("http")) {
    return url;
  }
  if (!url) {
    return environment.developmentUrl;
  }
  if (url[0] === "/") {
    return environment.developmentUrl + url.slice(1);
  }
  return environment.developmentUrl + url;
};

// True when the resolved (absolute) URL targets the portal/identity API and so
// may carry the bearer token. buildUrl is idempotent on absolute URLs, so this
// is safe to call on the already-rewritten request in the retry path.
const shouldAttachToken = (url: string): boolean =>
  shouldIncludeToken.some(base => buildUrl(url).startsWith(base));

export const authInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const auth = inject(AuthenticationService);
  const authMode = inject(AuthModeService);
  const globalErrorHandler = inject(ErrorHandler);
  const store = inject(Store<AppState>);

  const attachCookieCredentials = (
    req: HttpRequest<unknown>
  ): HttpRequest<unknown> => {
    const url = buildUrl(req.url);
    const isPortalApi =
      url.startsWith(environment.developmentUrl) ||
      url.startsWith(environment.identityDevelopmentUrl);
    if (!isPortalApi) {
      return req;
    }
    return req.clone({ url, withCredentials: true });
  };

  const addAuthenticationToken = (
    req: HttpRequest<unknown>,
    token: string
  ): HttpRequest<unknown> => {
    const url = buildUrl(req.url);

    if (!shouldAttachToken(url)) {
      return req;
    }

    return req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
      url,
    });
  };

  const spinner = request.params.get("spinner");
  const showSpinner = !(spinner && spinner === "no");
  let modifiedRequest = request;
  if (spinner) {
    modifiedRequest = request.clone({
      params: request.params.delete("spinner"),
    });
  }
  if (
    showSpinner &&
    (request.url.startsWith(environment.developmentUrl) ||
      request.url.startsWith(environment.identityDevelopmentUrl))
  ) {
    store.dispatch(LoaderActions.startLoader());
  }

  const stopSpinnerOnResponse = (event: HttpEvent<unknown>) => {
    if (event instanceof HttpResponse && showSpinner) {
      store.dispatch(LoaderActions.stopLoader());
    }
    return event;
  };

  if (authMode.mode === "portal") {
    const cookieReq = attachCookieCredentials(modifiedRequest);
    return next(cookieReq).pipe(
      map(stopSpinnerOnResponse),
      catchError((error: Error) => {
        if (showSpinner) {
          store.dispatch(LoaderActions.stopLoader());
        }
        if (error instanceof HttpErrorResponse && error.status === 401) {
          return handlePortalUnauthorized(
            cookieReq,
            next,
            auth,
            globalErrorHandler
          );
        }
        globalErrorHandler.handleError(error);
        return throwError(() => error);
      })
    );
  }

  return from(auth.accessToken()).pipe(
    switchMap(token => {
      const authReq = addAuthenticationToken(modifiedRequest, token);
      return next(authReq).pipe(
        map(stopSpinnerOnResponse),
        catchError((error: Error) => {
          if (showSpinner) {
            store.dispatch(LoaderActions.stopLoader());
          }

          // Handle 401 Unauthorized errors
          if (error instanceof HttpErrorResponse && error.status === 401) {
            return handleUnauthorizedError(authReq, next, auth);
          }

          globalErrorHandler.handleError(error);
          return throwError(() => error);
        })
      );
    })
  );
};

/**
 * Retries a request with a new authentication token
 */
const retryRequestWithNewToken = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  auth: AuthenticationService
): Observable<HttpEvent<unknown>> => {
  // Re-attach the token only for portal/identity URLs, mirroring the
  // first-attempt gate in addAuthenticationToken. Without this an external SAS
  // request that returned 401 would be retried with the portal bearer token
  // attached, leaking it across the storage trust boundary.
  if (!shouldAttachToken(request.url)) {
    return next(request);
  }

  return from(auth.accessToken()).pipe(
    mergeMap(token => {
      const newRequest = request.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
        },
      });
      return next(newRequest);
    })
  );
};

/**
 * Handles 401 Unauthorized errors by attempting to refresh the token and retry the request
 */
const handleUnauthorizedError = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  auth: AuthenticationService
): Observable<HttpEvent<unknown>> => {
  // First try to refresh the token
  return from(auth.refresh()).pipe(
    mergeMap(isRefreshSuccessful => {
      if (isRefreshSuccessful) {
        // If refresh was successful, retry the request with new token
        return retryRequestWithNewToken(request, next, auth);
      } else {
        // If refresh failed, try silent login
        // login() returns true (redirect initiated) or false (loginFlowInProgress
        // guard — a redirect is already in flight). In both cases the page is
        // navigating away, so silently abandon the request rather than retrying
        // with the stale token and surfacing an error notification before the
        // redirect lands.
        return from(auth.login()).pipe(mergeMap(() => EMPTY));
      }
    })
  );
};

/**
 * Portal-mode 401 handler: refresh once and retry the same (cookie-credentialed)
 * request. A second 401 redirects to the API login flow; other retry errors
 * flow through globalErrorHandler, symmetric with the outer catchError's
 * non-401 path on the original request.
 */
const handlePortalUnauthorized = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  auth: AuthenticationService,
  globalErrorHandler: ErrorHandler
): Observable<HttpEvent<unknown>> => {
  return from(auth.refresh()).pipe(
    // refresh() resolves for every HTTP outcome, so a rejection means the
    // cookie was re-minted but a local side effect threw afterwards. The
    // session is healthy, so report the error rather than redirect to login.
    catchError((error: Error) => {
      globalErrorHandler.handleError(error);
      return throwError(() => error);
    }),
    mergeMap(isRefreshSuccessful => {
      if (!isRefreshSuccessful) {
        void auth.login();
        return throwError(
          () => new Error("Authentication failed; redirecting to login")
        );
      }
      return next(request).pipe(
        catchError((error: Error) => {
          if (error instanceof HttpErrorResponse && error.status === 401) {
            void auth.login();
          } else {
            globalErrorHandler.handleError(error);
          }
          return throwError(() => error);
        })
      );
    })
  );
};
